import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
        <Link to="/" className="text-primary-600 hover:text-primary-700">返回首页</Link>
      </div>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbData = [
    { name: '首页', item: 'https://17c.com' },
    { name: category?.name || '', item: `https://17c.com/category/${article.categoryId}` },
    { name: article.title, item: `https://17c.com/article/${article.slug}` },
  ];

  const renderContent = (content: string) => {
    const lines = content.trim().split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;
    let codeBlock = '';
    let inCode = false;

    while (i < lines.length) {
      const line = lines[i];

      if (line.trim().startsWith('```')) {
        if (inCode) {
          elements.push(
            <pre key={`code-${i}`}><code>{codeBlock.trim()}</code></pre>
          );
          codeBlock = '';
          inCode = false;
        } else {
          inCode = true;
        }
        i++;
        continue;
      }

      if (inCode) {
        codeBlock += line + '\n';
        i++;
        continue;
      }

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={`h2-${i}`} id={line.replace('## ', '').replace(/\s+/g, '-').toLowerCase()}>
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('### ')) {
        elements.push(
          <h3 key={`h3-${i}`}>{line.replace('### ', '')}</h3>
        );
      } else if (line.startsWith('- ')) {
        elements.push(
          <li key={`li-${i}`}>{line.replace('- ', '')}</li>
        );
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(
          <p key={`p-${i}`}><strong>{line.replace(/\*\*/g, '')}</strong></p>
        );
      } else if (line.startsWith('> ')) {
        elements.push(
          <blockquote key={`bq-${i}`}><p>{line.replace('> ', '')}</p></blockquote>
        );
      } else if (line.trim() === '') {
        elements.push(<br key={`br-${i}`} />);
      } else {
        const html = line
          .replace(/`([^`]+)`/g, '<code>$1</code>')
          .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
          .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer nofollow">$1</a>');
        elements.push(
          <p key={`p-${i}`} dangerouslySetInnerHTML={{ __html: html }} />
        );
      }
      i++;
    }

    return elements;
  };

  return (
    <>
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: article.author,
          datePublished: article.publishDate,
          dateModified: article.publishDate,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: article.title,
          description: article.excerpt,
          url: `https://17c.com/article/${article.slug}`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbData.map((item, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: item.name,
              item: item.item,
            })),
          },
        }}
      />
      <SchemaOrg type="BreadcrumbList" data={{ items: breadcrumbData }} />

      <article className="bg-background-50">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '', href: `/category/${article.categoryId}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 min-w-0 max-w-[800px]">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {category?.name}
                  </Link>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-600 text-sm"></i>
                    </div>
                    <span>{article.author}</span>
                  </div>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    <span>{article.publishDate}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap mt-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 border border-background-200/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="article-content text-sm md:text-base">
                {renderContent(article.content)}
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground-800 mb-1">分享文章</h4>
                    <div className="flex items-center gap-2">
                      <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-link-m"></i>
                      </button>
                      <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-fill"></i>
                      </button>
                      <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-wechat-fill"></i>
                      </button>
                    </div>
                  </div>
                  <Link
                    to={`/category/${article.categoryId}`}
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
                  >
                    返回 {category?.name}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>

            <aside className="lg:w-[300px] shrink-0">
              <div className="sticky top-24">
                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70 mb-6">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-article-line text-primary-500"></i>
                    相关文章
                  </h4>
                  {relatedArticles.length > 0 ? (
                    <div className="flex flex-col gap-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.id}
                          to={`/article/${ra.slug}`}
                          className="group cursor-pointer"
                        >
                          <h5 className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-2 mb-1">
                            {ra.title}
                          </h5>
                          <span className="text-xs text-foreground-400">{ra.publishDate}</span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-foreground-400">暂无相关文章</p>
                  )}
                </div>

                <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-price-tag-3-line text-primary-500"></i>
                    标签
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-2.5 py-1 rounded-full bg-background-50 text-foreground-600 border border-background-200/50"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </>
  );
}